import React, { Component } from 'react';
import { connect } from 'react-redux';
import { VideoLoadingPlaceholder } from './CultureFitModule.style';
import { getCultureFitVideoId } from 'utils/module-identificators';

import VideoComponent from 'components/shared/VideoComponent/VideoComponent';
import CultureFitIntroduction from './CultureFitIntroduction/CultureFitIntroduction';
import Logger from 'services/Logger';

import { RotateSpinLoader } from 'react-css-loaders';
const videoId = getCultureFitVideoId();

// Hard cap on how long we wait for the preloader before falling back to the
// streaming URL directly. Covers the case where the preloader entry never
// settles (e.g., the preload action was never dispatched because of a quirk
// upstream) and the user would otherwise be stuck on a spinner forever.
// 5s is short enough that a stuck user recovers quickly without feeling
// abandoned; for healthy preloads it's never reached because the entry
// becomes ready within 1-2s.
const PRELOAD_FALLBACK_TIMEOUT_MS = 5000;

export class CultureFitModule extends Component {
    constructor(props) {
        super(props);

        this.state = {
            introductionAccepted: false,
        };

        this.acceptIntroduction = this.acceptIntroduction.bind(this);
        this.getPreloadedVideo = this.getPreloadedVideo.bind(this);
    }

    getPreloadedVideo() {
        if (this.state.videoUrl) return; // already resolved

        const { preloader, videoUrl: streamingUrl } = this.props;
        const entry = preloader && videoId && preloader[videoId];

        // Preloader succeeded — use the buffered blob URL.
        if (entry && entry.ready) {
            this.setState({
                videoUrl: entry.url,
                fallbackVideoUrl: entry.fallbackUrl,
            });
            return;
        }

        // Preloader settled but failed (loading=false and ready=false).
        // Fall back to the streaming URL passed in by the parent so the
        // user doesn't get stuck on a spinner. The browser will stream
        // the video directly; slightly slower start, no other downside.
        if (
            entry &&
            entry.loading === false &&
            !entry.ready &&
            streamingUrl
        ) {
            Logger.logWarning(
                'CultureFitModule - preload failed, falling back to streaming URL',
                {
                    videoId,
                    streamingUrl,
                    error:
                        entry.error && entry.error.message
                            ? entry.error.message
                            : entry.error,
                },
            );
            this.setState({
                videoUrl: streamingUrl,
                fallbackVideoUrl: streamingUrl,
            });
        }
    }

    componentDidMount() {
        this.getPreloadedVideo();

        // Safety net: if neither the preloader nor the failed-state branch
        // above has set a videoUrl within the timeout window, force the
        // streaming-URL fallback. Catches the case where preloader[videoId]
        // never gets created (e.g., upstream threw before dispatching) so the
        // user is never indefinitely stuck.
        this.preloadFallbackTimeout = setTimeout(() => {
            if (this.state.videoUrl) return;
            const { videoUrl: streamingUrl } = this.props;
            if (!streamingUrl) return;
            Logger.logWarning(
                'CultureFitModule - preload timeout, falling back to streaming URL',
                { videoId, streamingUrl },
            );
            this.setState({
                videoUrl: streamingUrl,
                fallbackVideoUrl: streamingUrl,
            });
        }, PRELOAD_FALLBACK_TIMEOUT_MS);
    }

    componentDidUpdate() {
        this.getPreloadedVideo();
    }

    componentWillUnmount() {
        if (this.preloadFallbackTimeout) {
            clearTimeout(this.preloadFallbackTimeout);
        }
    }

    acceptIntroduction() {
        this.setState({
            introductionAccepted: true,
        });
    }

    render() {
        const { introductionAccepted, videoUrl, fallbackVideoUrl } = this.state;
        return (
            <React.Fragment>
                {!introductionAccepted && (
                    <CultureFitIntroduction
                        isOptOut={this.props.isOptOut}
                        onNext={() => this.acceptIntroduction()}
                    />
                )}
                {introductionAccepted && videoUrl && (
                    <VideoComponent
                        videoId={videoId}
                        videoUrl={videoUrl}
                        fallbackVideoUrl={fallbackVideoUrl}
                        playAutomatically={true}
                        onStartPlaying={this.props.onStartRecording}
                        onComplete={this.props.onComplete}
                        isVideoFullscreen={true}
                    />
                )}
                {introductionAccepted && !videoUrl && (
                    <VideoLoadingPlaceholder visible={!videoUrl}>
                        <RotateSpinLoader color="#fff" size="2" />
                    </VideoLoadingPlaceholder>
                )}
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state) => ({
    preloader: state.preloader.videos,
});

export default connect(mapStateToProps)(CultureFitModule);
